import { REPEATABLES, SPECIALIZATIONS, TECHNOLOGIES } from "./content/research.js";
import type { TechnologyEffect } from "./content/types.js";
import {
  formatMicro,
  multiplyRational,
  type Rational,
  rational,
  rationalFromDecimal,
  settleMicroFloor,
} from "./exact-numbers.js";
import type { ResearchEffects, ResearchState } from "./research-types.js";

export function effectsForResearch(state: ResearchState): ResearchEffects {
  const multipliers: Record<string, Rational> = {};
  const ratios: Record<string, string> = {
    "restoration-metal": "0.5",
    "raider-infrastructure-leak": "0.2",
  };
  const unlocks = new Set<string>([
    "mining-rig",
    "power-generator",
    "construction-yard",
    "robot-factory",
    "research-lab",
    "shield-generator",
    "supply-depot",
    "guard-robot",
    "raider-robot",
  ]);
  for (const id of state.completed)
    applyEffect(requiredEffect(TECHNOLOGIES, id), multipliers, ratios, unlocks);
  for (const id of [state.industrialSpecialization, state.militarySpecialization]) {
    if (id) applyEffect(requiredEffect(SPECIALIZATIONS, id), multipliers, ratios, unlocks);
  }
  for (const line of REPEATABLES) {
    const level = state.repeatableLevels[line.id];
    for (let index = 0; index < level; index += 1)
      applyEffect(
        { kind: "multiply", factor: line.factorPerLevel, targets: line.targets },
        multipliers,
        ratios,
        unlocks,
      );
  }
  return Object.freeze({
    multipliers: Object.freeze(
      Object.fromEntries(
        Object.entries(multipliers).map(([id, value]) => [
          id,
          formatMicro(settleMicroFloor(value)),
        ]),
      ),
    ),
    ratios: Object.freeze(ratios),
    unlocks,
  });
}

export function effectMultiplier(effects: ResearchEffects, target: string): string {
  return effects.multipliers[target] ?? "1";
}

function applyEffect(
  effect: TechnologyEffect,
  multipliers: Record<string, Rational>,
  ratios: Record<string, string>,
  unlocks: Set<string>,
): void {
  if (effect.kind === "unlock") {
    for (const target of effect.targets) unlocks.add(target);
  } else if (effect.kind === "multiply") {
    for (const target of effect.targets) {
      const current = multipliers[target] ?? rational(1n);
      multipliers[target] = multiplyRational(current, rationalFromDecimal(effect.factor));
    }
  } else if (effect.kind === "restore-metal-ratio") ratios["restoration-metal"] = effect.ratio;
  else ratios[effect.target] = effect.ratio;
}

function requiredEffect(
  values: readonly { readonly id: string; readonly effect: TechnologyEffect }[],
  id: string,
): TechnologyEffect {
  const value = values.find((entry) => entry.id === id);
  if (!value) throw new TypeError(`Unknown research effect: ${id}`);
  return value.effect;
}
